import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, ExternalLink, AlertTriangle } from 'lucide-react';
import Button from '../../../shared/components/ui/Button';
import BookMeetingModal from './BookMeetingModal';
import SiteVisitOutcomeModal from '../siteVisit/SiteVisitOutcomeModal';
import { awaitsOutcome, outcomeBadge } from '../siteVisit/siteVisitOutcome';
import { getSchedulingLeadMeetings } from '../../services/api/implementation/impl';
import { schedulingLeadMeetingsQueryKey, providerLabel } from '../../utils/schedulingAccess';
import useSchedulingIntegration from '../../hooks/useSchedulingIntegration';

const REMINDER_STATUS_STYLES = {
  sent: 'text-green-700',
  pending: 'text-gray-600',
  processing: 'text-gray-600',
  failed: 'text-red-700',
  cancelled: 'text-gray-400',
  skipped: 'text-gray-400'
};

function formatWhen(iso, timeZone) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  try {
    return new Intl.DateTimeFormat('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
      ...(timeZone ? { timeZone } : {})
    }).format(date);
  } catch {
    // An invalid IANA zone from the backend shouldn't blank out the whole row.
    return new Intl.DateTimeFormat('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
  }
}

function offsetLabel(minutes) {
  if (minutes >= 1440) {
    const days = Math.round(minutes / 1440);
    return days === 1 ? '24h before' : `${days}d before`;
  }
  if (minutes >= 60) {
    const hours = Math.round(minutes / 60);
    return hours === 1 ? '1h before' : `${hours}h before`;
  }
  return `${minutes}m before`;
}

export default function LeadMeetingsPanel({ leadId, lead }) {
  const [isBookModalOpen, setIsBookModalOpen] = useState(false);
  const [outcomeMeeting, setOutcomeMeeting] = useState(null);

  const {
    connected,
    provider,
    canBookInApp,
    canSchedule,
    isPending: isIntegrationPending,
    data: integration
  } = useSchedulingIntegration();

  const {
    data: meetings,
    isLoading,
    isError
  } = useQuery({
    queryKey: schedulingLeadMeetingsQueryKey(leadId),
    queryFn: () => getSchedulingLeadMeetings(leadId),
    enabled: Boolean(leadId) && connected
  });

  if (isIntegrationPending) {
    return (
      <div className="mt-2 flex min-h-[10rem] items-center justify-center rounded-2xl border border-slate-200/90 bg-white text-sm text-slate-500">
        Loading…
      </div>
    );
  }

  if (!connected) {
    return (
      <div className="mt-2 flex min-h-[10rem] flex-col items-center justify-center gap-2 rounded-2xl border border-slate-200/90 bg-white px-6 text-center">
        <CalendarClock className="h-8 w-8 text-slate-300" />
        <p className="text-sm font-medium text-slate-700">WhatsApp scheduling is not connected</p>
        <p className="max-w-md text-xs text-slate-500">
          Connect WhatsApp booking under Integrations so this lead can pick a demo time on WhatsApp.
        </p>
      </div>
    );
  }

  const activeMeetings = (meetings || []).filter((m) => m.status === 'active');
  const pastOrCancelled = (meetings || []).filter((m) => m.status !== 'active');

  return (
    <div className="mt-2 w-full space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200/90 bg-white px-5 py-4">
        <div>
          <p className="text-base font-semibold text-slate-900">Meetings</p>
          <p className="text-sm text-slate-500">
            {integration?.defaultServiceName
              ? `via ${providerLabel(provider)} · ${integration.defaultServiceName}`
              : `via ${providerLabel(provider)}`}
          </p>
        </div>
        <Button
          displayType="primary"
          disabled={!canSchedule}
          title={
            canSchedule
              ? undefined
              : `${providerLabel(provider)} offers no way to book from here.`
          }
          onClick={() => setIsBookModalOpen(true)}>
          {canBookInApp ? 'Book a meeting' : 'Send booking link'}
        </Button>
      </div>

      {connected && !integration?.webhookConfigured && provider !== 'pinggo' ? (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-5 py-4 text-sm text-amber-950">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <p className="text-sm leading-relaxed">
            No webhook is configured, so meetings booked or cancelled outside Housingram will not
            appear here. Set it up under Integrations → Scheduling.
          </p>
        </div>
      ) : null}

      {isLoading ? (
        <div className="rounded-2xl border border-slate-200/90 bg-white px-5 py-6 text-sm text-slate-500">
          Loading meetings…
        </div>
      ) : isError ? (
        <div className="rounded-2xl border border-slate-200/90 bg-white px-5 py-6 text-sm text-red-700">
          Could not load meetings for this lead.
        </div>
      ) : !meetings?.length ? (
        <div className="rounded-2xl border border-slate-200/90 bg-white px-5 py-8 text-center text-sm text-slate-500">
          No meetings booked yet.
        </div>
      ) : (
        <>
          {activeMeetings.map((meeting) => (
            <MeetingCard
              key={meeting.id}
              meeting={meeting}
              onRecordOutcome={() => setOutcomeMeeting(meeting)}
            />
          ))}
          {pastOrCancelled.length ? (
            <div className="pt-2">
              <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Cancelled
              </p>
              {pastOrCancelled.map((meeting) => (
                <MeetingCard key={meeting.id} meeting={meeting} />
              ))}
            </div>
          ) : null}
        </>
      )}

      {isBookModalOpen ? (
        <BookMeetingModal
          leadId={leadId}
          lead={lead}
          // Capability decides the shape, not the provider's name.
          mode={canBookInApp ? 'slots' : 'link'}
          onClose={() => setIsBookModalOpen(false)}
        />
      ) : null}

      {outcomeMeeting ? (
        <SiteVisitOutcomeModal
          leadId={leadId}
          leadName={lead?.user?.name}
          visitAt={outcomeMeeting.start_time}
          onClose={() => setOutcomeMeeting(null)}
        />
      ) : null}
    </div>
  );
}

function MeetingCard({ meeting, onRecordOutcome }) {
  const cancelled = meeting.status !== 'active';
  const badge = outcomeBadge(meeting.outcome);

  return (
    <div
      className={`mb-3 rounded-2xl border px-5 py-4 ${
        cancelled ? 'border-slate-200 bg-slate-50' : 'border-slate-200/90 bg-white'
      }`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p
            className={`text-base font-semibold ${
              cancelled ? 'text-slate-500 line-through' : 'text-slate-900'
            }`}>
            {meeting.service_name || 'Meeting'}
          </p>
          <p className="mt-0.5 text-sm text-slate-600">
            {formatWhen(meeting.start_time, meeting.invitee_timezone)}
            {meeting.invitee_timezone ? (
              <span className="text-slate-400"> · {meeting.invitee_timezone}</span>
            ) : null}
          </p>
          {meeting.invitee_name || meeting.invitee_email ? (
            <p className="mt-1 text-sm text-slate-500">
              {meeting.invitee_name}
              {meeting.invitee_name && meeting.invitee_email ? ' · ' : ''}
              {meeting.invitee_email}
            </p>
          ) : null}
        </div>

        <div className="flex flex-shrink-0 flex-col items-end gap-1">
          {badge ? (
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${badge.className}`}>
              {badge.label}
            </span>
          ) : cancelled ? (
            <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-600">
              Cancelled
            </span>
          ) : (
            <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800">
              Booked
            </span>
          )}
          {meeting.attribution_source === 'invitee_email' ? (
            <span
              className="text-xs text-amber-700"
              title="Matched to this lead by email rather than by a link we sent.">
              matched by email
            </span>
          ) : null}
        </div>
      </div>

      {meeting.location_value && !cancelled ? (
        <a
          href={meeting.location_value}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E4A53] underline">
          <ExternalLink className="h-3.5 w-3.5" />
          {meeting.location_type === 'physical' ? meeting.location_value : 'Join meeting'}
        </a>
      ) : null}

      {cancelled && meeting.cancel_reason ? (
        <p className="mt-2 text-sm text-slate-500">Reason: {meeting.cancel_reason}</p>
      ) : null}

      {meeting.outcome_note ? (
        <p className="mt-2 text-sm text-slate-600">Note: {meeting.outcome_note}</p>
      ) : null}

      {onRecordOutcome && awaitsOutcome(meeting) ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
          <p className="text-sm font-medium text-amber-900">
            The visit time has passed. Record what happened.
          </p>
          <button
            type="button"
            onClick={onRecordOutcome}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90">
            Record outcome
          </button>
        </div>
      ) : null}

      {!cancelled && !meeting.outcome && meeting.reminders?.length ? (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Reminders
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {meeting.reminders.map((reminder) => (
              <span
                key={reminder.id}
                className={`text-xs ${
                  REMINDER_STATUS_STYLES[reminder.status] || 'text-slate-600'
                }`}
                title={reminder.error_message || undefined}>
                {reminder.channel} · {offsetLabel(reminder.offset_minutes)} · {reminder.status}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {!cancelled && meeting.reschedule_url ? (
        <a
          href={meeting.reschedule_url}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-block text-sm font-semibold text-slate-500 underline">
          Reschedule
        </a>
      ) : null}
    </div>
  );
}
